"use client";

import { useRef } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { useLanguage } from "@/components/LanguageProvider";
import { getHyundaiCampaignCars, getMazdaCampaignCars, type CampaignCar } from "@/lib/campaignCars";
import { SYMBOL_PATH, WORDMARK_PATH } from "@/components/MazdaLogoReveal";

type Brand = {
  key: "mazda" | "hyundai";
  name: string;
  href: string;
  /** Logo colour on white; turns white over the film on hover. */
  colorClass: string;
  /** Width of the whole mark (the wordmark is the widest part). */
  widthClass: string;
  video: string | undefined;
  fontClass: string;
};

const firstVideo = (cars: CampaignCar[]) => {
  const src = cars[0]?.videoSrc;
  return Array.isArray(src) ? src[0] : src;
};

// Official Mazda files (v25.1). Hyundai: vertical mark from hyundai.com – swap for
// Hyundai's official colour/white files when the dealer gets access to them.
const BRANDS: Brand[] = [
  {
    key: "mazda",
    name: "Mazda",
    href: "/mazda",
    colorClass: "text-black",
    widthClass: "w-28 sm:w-32 md:w-40",
    video: firstVideo(getMazdaCampaignCars()),
    fontClass: "brand-mazda",
  },
  {
    key: "hyundai",
    name: "Hyundai",
    href: "/hyundai",
    colorClass: "text-[#002C5F]",
    widthClass: "w-32 sm:w-40 md:w-52",
    video: firstVideo(getHyundaiCampaignCars()),
    fontClass: "font-hyundai",
  },
];

const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * Two-step reveal of the official logos. Both parts stay exactly where they sit
 * in the official artwork (brand rules: never change the balance between symbol
 * and wordmark). Only visibility is animated: the symbol fades in, then the
 * wordmark is wiped in from the centre, in place.
 */
const SYMBOL_VARIANTS = {
  hidden: { opacity: 0 },
  shown: (delay: number) => ({ opacity: 1, transition: { duration: 0.9, delay, ease: EASE } }),
};

// Mazda vertical logo v25.1: symbol and wordmark paths in their original coordinates.
const MAZDA_VIEWBOX = { x: 42.5, y: 42.5, w: 283.5, h: 251.2 };
const MAZDA_WORDMARK = { x: 42.5, y: 250.5, w: 283.5, h: 43.2 };

function MazdaMark({ delay }: { delay: number }) {
  const { x, y, w, h } = MAZDA_VIEWBOX;
  const wm = MAZDA_WORDMARK;
  const clipId = `mazda-wordmark-${Math.round(delay * 100)}`;
  return (
    <svg viewBox={`${x} ${y} ${w} ${h}`} className="h-auto w-full" aria-hidden>
      <defs>
        <clipPath id={clipId}>
          <motion.rect
            y={wm.y - 2}
            height={wm.h + 4}
            variants={{
              hidden: { x: wm.x + wm.w / 2, width: 0 },
              shown: { x: wm.x, width: wm.w, transition: { duration: 0.8, delay: delay + 0.7, ease: EASE } },
            }}
          />
        </clipPath>
      </defs>
      <motion.path d={SYMBOL_PATH} fill="currentColor" variants={SYMBOL_VARIANTS} custom={delay} />
      <path d={WORDMARK_PATH} fill="currentColor" clipPath={`url(#${clipId})`} />
    </svg>
  );
}

// Hyundai vertical logo (hyundai.com) as one mask; the wordmark starts at 68.6 % height.
const HYUNDAI_SPLIT = 68.6;
const hyundaiMask = {
  WebkitMaskImage: "url(/assets/hyundai/hyundai-logo-vertical-mask.png)",
  maskImage: "url(/assets/hyundai/hyundai-logo-vertical-mask.png)",
  WebkitMaskSize: "100% 100%",
  maskSize: "100% 100%",
  backgroundColor: "currentColor",
} as const;

function HyundaiMark({ delay }: { delay: number }) {
  return (
    <div className="relative aspect-[1058/613] w-full" aria-hidden>
      <motion.div
        className="absolute inset-0"
        style={{ ...hyundaiMask, clipPath: `inset(0% 0% ${100 - HYUNDAI_SPLIT}% 0%)` }}
        variants={SYMBOL_VARIANTS}
        custom={delay}
      />
      <motion.div
        className="absolute inset-0"
        style={hyundaiMask}
        variants={{
          hidden: { clipPath: `inset(${HYUNDAI_SPLIT}% 50% 0% 50%)` },
          shown: {
            clipPath: `inset(${HYUNDAI_SPLIT}% 0% 0% 0%)`,
            transition: { duration: 0.8, delay: delay + 0.7, ease: EASE },
          },
        }}
      />
    </div>
  );
}

function BrandMark({ brand, delay }: { brand: Brand; delay: number }) {
  return brand.key === "mazda" ? <MazdaMark delay={delay} /> : <HyundaiMark delay={delay} />;
}

function BrandTile({ brand, index }: { brand: Brand; index: number }) {
  const { language } = useLanguage();
  const videoRef = useRef<HTMLVideoElement>(null);
  const reduceMotion = useReducedMotion();

  const play = () => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;
    video.play().catch(() => {});
  };
  const pause = () => videoRef.current?.pause();

  return (
    <Link
      href={brand.href}
      onMouseEnter={play}
      onMouseLeave={pause}
      onFocus={play}
      onBlur={pause}
      className={`group relative flex h-[22vh] min-h-[160px] items-center justify-center overflow-hidden bg-white outline-none md:h-[30vh] ${brand.fontClass}`}
      aria-label={language === "sv" ? `Utforska ${brand.name}` : `Explore ${brand.name}`}
    >
      {brand.video && !reduceMotion ? (
        <video
          ref={videoRef}
          src={brand.video}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden
          className="absolute inset-0 h-full w-full scale-105 object-cover opacity-0 transition duration-700 ease-out group-hover:scale-100 group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      ) : null}
      {/* Dims the film so the logo sits on a calm background (Mazda brand rule). */}
      <div
        aria-hidden
        className="absolute inset-0 bg-black opacity-0 transition duration-700 group-hover:opacity-65 group-focus-visible:opacity-65"
      />

      <motion.div
        initial="hidden"
        whileInView="shown"
        viewport={{ once: true, amount: 0.5 }}
        className="relative z-10 flex flex-col items-center gap-5"
      >
        <div
          role="img"
          aria-label={brand.name}
          className={`${brand.widthClass} ${brand.colorClass} transition-[color,transform] duration-700 ease-out group-hover:-translate-y-2 group-hover:text-white group-focus-visible:text-white`}
        >
          <BrandMark brand={brand} delay={0.1 + index * 0.25} />
        </div>
        <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-foreground/70 transition duration-500 md:translate-y-2 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:text-white md:group-hover:opacity-100 md:group-focus-visible:opacity-100">
          {language === "sv" ? `Utforska ${brand.name}` : `Explore ${brand.name}`}
          <ArrowRight className="h-3.5 w-3.5" aria-hidden />
        </span>
      </motion.div>
    </Link>
  );
}

/** Clickable Mazda / Hyundai logos under the homepage hero. */
export function BrandLogoStrip() {
  const { language } = useLanguage();

  return (
    <section aria-label={language === "sv" ? "Våra märken" : "Our brands"} className="relative border-t border-border bg-white">
      <div className="grid grid-cols-2">
        {BRANDS.map((brand, index) => (
          <BrandTile key={brand.key} brand={brand} index={index} />
        ))}
      </div>
      <motion.div
        aria-hidden
        initial={{ scaleY: 0 }}
        whileInView={{ scaleY: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none absolute inset-y-[18%] left-1/2 w-px origin-top bg-border"
      />
    </section>
  );
}
